import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard, purgeTestEntries } from "@/lib/adminApi";
import { PageHeader, StatCard, Chip, FORM_LABEL, FORM_TONE, fmtDate, fmtMoney, ageBadge } from "./_shared";
import { ArrowRight, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { toast } from "sonner";

function MiniBarChart({ series }) {
  if (!series || !series.length) {
    return <div className="text-xs text-slate-400">No paid registrations yet.</div>;
  }
  const max = Math.max(...series.map((s) => s.count));
  return (
    <div className="flex h-32 items-end gap-1.5">
      {series.map((s) => {
        const h = max ? (s.count / max) * 100 : 0;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-t-md bg-gradient-to-t from-lime-500 to-lime-300 transition-all" style={{ height: `${h}%`, minHeight: s.count ? "6px" : "0" }} />
            <div className="text-[9px] text-slate-400">{s.date.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard().then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const purge = async () => {
    if (!window.confirm("Delete every entry that looks like a test row (emails ending in @example.com / @test.com, names starting with TEST or QA)? This is permanent.")) return;
    try {
      const res = await purgeTestEntries();
      toast.success(`Purged ${res.deleted} test entries`);
      const fresh = await fetchDashboard();
      setData(fresh);
    } catch {
      toast.error("Purge failed");
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading dashboard…</div>;
  if (!data) return <div className="text-sm text-rose-600">Failed to load dashboard.</div>;

  const inr = data.revenue?.INR;
  const usd = data.revenue?.USD;
  const totalRevenueInr = (inr?.total || 0);
  const totalRevenueUsd = (usd?.total || 0);

  const conversionUp = data.conversion_rate >= 5;
  const abandonedCount = data.delegates.abandoned;
  const paidCount = data.delegates.paid;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        title="Command Center"
        subtitle={`Live event pipeline · ${data.total_leads.toLocaleString()} total leads · ${paidCount} confirmed delegates`}
        actions={
          <button data-testid="purge-test" onClick={purge} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200">
            <Sparkles className="h-3.5 w-3.5" /> Purge test entries
          </button>
        }
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="kpi-revenue-inr" tone="navy" label="Revenue (INR)" value={fmtMoney(totalRevenueInr, "INR")} sub={`${inr?.count || 0} paid delegates`} />
        <StatCard testid="kpi-revenue-usd" label="Revenue (USD)" value={fmtMoney(totalRevenueUsd, "USD")} sub={`${usd?.count || 0} international`} />
        <StatCard testid="kpi-paid" tone="lime" label="Confirmed Delegates" value={paidCount} sub="Paid & confirmed" />
        <StatCard testid="kpi-abandoned" tone="amber" label="Abandoned Checkouts" value={abandonedCount} sub="Recovery opportunity" />
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Brochure Downloads" value={data.counts.brochure || 0} sub="Warm leads" />
        <StatCard label="Rate Card / Proposal" value={(data.counts.sponsorship_proposal || 0) + (data.counts.prospectus || 0)} sub="Sponsor interest" />
        <StatCard label="Market Report" value={data.counts.market_report || 0} sub="Deep-intent leads" />
        <StatCard label="Newsletter" value={data.counts.newsletter || 0} sub="Top of funnel" />
      </div>

      {/* Trend + Conversion */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Paid delegates — last 14 days</div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">Registration momentum</div>
            </div>
            <button data-testid="dash-go-delegates" onClick={() => nav("/admin/delegates")} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">
              View all →
            </button>
          </div>
          <div className="mt-6"><MiniBarChart series={data.trend} /></div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Conversion rate</div>
          <div className="mt-1 font-display text-4xl font-bold text-slate-900">{data.conversion_rate}%</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            {conversionUp ? <TrendingUp className="h-3.5 w-3.5 text-lime-600" /> : <TrendingDown className="h-3.5 w-3.5 text-amber-600" />}
            Leads → paid delegates
          </div>
          <div className="mt-5 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-lime-500 to-lime-300" style={{ width: `${Math.min(data.conversion_rate, 100)}%` }} />
          </div>
          <div className="mt-4 space-y-1.5 text-xs text-slate-600">
            <div className="flex justify-between"><span>Total leads</span><span className="font-semibold">{data.total_leads}</span></div>
            <div className="flex justify-between"><span>Paid</span><span className="font-semibold text-lime-700">{paidCount}</span></div>
            <div className="flex justify-between"><span>Abandoned</span><span className="font-semibold text-amber-700">{abandonedCount}</span></div>
            <div className="flex justify-between"><span>Failed</span><span className="font-semibold text-rose-700">{data.delegates.failed}</span></div>
          </div>
        </div>
      </div>

      {/* Hot leads feed */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-lime-600">Live feed</div>
            <div className="font-display text-lg font-bold text-slate-900">Latest leads</div>
          </div>
          <button onClick={() => nav("/admin/leads")} className="text-xs font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1">
            All leads <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {data.hot_leads.map((h) => {
            const a = ageBadge(h.created_at);
            return (
              <div key={h.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition">
                <Chip tone={FORM_TONE[h.form_type] || "slate"}>{FORM_LABEL[h.form_type] || h.form_type}</Chip>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{h.name || h.email}</div>
                  <div className="text-xs text-slate-500 truncate">{h.email} · {h.organisation || "—"}</div>
                </div>
                <Chip tone={a.tone}>{a.label}</Chip>
              </div>
            );
          })}
          {!data.hot_leads.length && <div className="px-5 py-10 text-center text-sm text-slate-400">No leads yet.</div>}
        </div>
      </div>
    </div>
  );
}
